/**
 * Logo — official Ur-Nammu Publishing brand lockup (mark + wordmark).
 *
 * Renders a single <img> whose source is chosen by theme. Replaces the
 * previous LogoMark + BrandName pair: the wordmark is baked into the image,
 * so no adjacent text is needed.
 *
 * Flow:
 *   1. Read isDark from useTheme.
 *   2. Resolve `variant`: "auto" → isDark ? "dark" : "light"; otherwise force.
 *   3. Build src as `${import.meta.env.BASE_URL}brand/logo-${resolved}.png`
 *      (base-aware for subdirectory deploys; '/' in dev).
 *   4. Render <img> with fixed height, aspect-ratio CSS to prevent CLS,
 *      i18n-driven alt text, and eager loading (the logo is above-the-fold
 *      on every page that uses it).
 *
 * @layer-1 — shared component, use everywhere the brand mark appears.
 */
import { useTranslation } from "react-i18next";
import { useTheme } from "@/shared/hooks/useTheme";
import { cn } from "@/shared/lib/utils";

/** Aspect ratio of the final lockup image (width / height) — 990×562 frameless source. */
const LOGO_ASPECT_RATIO = 1.762;

interface LogoProps {
  /** Rendered height in px. Width is derived from the aspect ratio. Default 48. Ignored when fillWidth=true. */
  height?: number;
  /** "auto" (default) follows theme; "light"/"dark" force a specific variant. */
  variant?: "auto" | "light" | "dark";
  /** Extra className applied to the <img>. */
  className?: string;
  /** Fill available width; height derived from aspect ratio. Use in flex/grid containers. */
  fillWidth?: boolean;
}

/** Official Ur-Nammu brand lockup — theme-aware <img>. */
export function Logo({ height = 48, variant = "auto", className, fillWidth = false }: LogoProps) {
  const { isDark } = useTheme();
  const { t } = useTranslation("common");

  const resolved: "light" | "dark" =
    variant === "auto" ? (isDark ? "dark" : "light") : variant;

  return (
    <img
      src={`${import.meta.env.BASE_URL}brand/logo-${resolved}.png`}
      alt={t("brandName")}
      loading="eager"
      decoding="async"
      className={cn("select-none", className)}
      style={
        fillWidth
          ? { width: "100%", height: "auto", aspectRatio: `${LOGO_ASPECT_RATIO} / 1` }
          : { height: `${height}px`, width: "auto", aspectRatio: `${LOGO_ASPECT_RATIO} / 1` }
      }
    />
  );
}